import { router } from 'expo-router';
import { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { StyleSheet, View } from 'react-native';
import { Divider } from 'react-native-paper';

import { useGetNonProfitPaymentMethods } from '@/api/hooks/nonProfitOrgHooks';
import { Subscription } from '@/api/models/paymentAccountsModels';
import { TokenizedACHData } from '@/app/(app)/paymentMethods/achList';
import { TokenizedCreditCardData } from '@/app/(app)/paymentMethods/creditCardList';
import { giftRecurringOptions, predefinedValues } from '@/constants/options/DonationOptions';
import { EditSubscriptionData, editSubscriptionResolver } from '@/lib/validations/DonationSchema';
import { encryptObject } from '@/utils/crypto';
import { i18n } from '@/utils/i18n';
import { safeJsonParse } from '@/utils/jsonParse';

import BrandedChip from '../BrandedChip';
import BrandedButton from '../buttons/BrandedButton';
import CurrencyInputField from '../inputs/CurrencyInputField';
import DatePickerInputField from '../inputs/DateInputPickerField';
import TokneizedACHView from '../paymentMethods/TokenizedACHView';
import TokenizedCardView from '../paymentMethods/TokenizedCardView';
import Text from '../Text';

interface EditSubscriptionFormProps {
  initialValues: EditSubscriptionData;
  npoId: string;
  loading?: boolean;
  onSubmitTap: (data: EditSubscriptionData) => void;
  subscriptionItem?: string;
  tokenizedACH?: string;
  tokenizedCard?: string;
}

const EditSubscriptionForm = ({
  initialValues,
  npoId,
  loading,
  onSubmitTap,
  subscriptionItem,
  tokenizedACH,
  tokenizedCard
}: EditSubscriptionFormProps) => {
  // Strings
  const confirm = i18n.t('mix.confirm');

  const { mutateAsync: getNonProfitPaymentMethod, isPending } = useGetNonProfitPaymentMethods();

  const [parsedTokenizedCard, setTokenizedCard] = useState(safeJsonParse<TokenizedCreditCardData>(tokenizedCard ?? ''));
  const [parsedTokenizedACH, setTokenizedACH] = useState(safeJsonParse<TokenizedACHData>(tokenizedACH ?? ''));
  const parsedSubscriptionItem = safeJsonParse<Subscription>(subscriptionItem ?? '');

  const {
    control,
    handleSubmit,
    formState: { isValid },
    getValues,
    setValue,
    setError,
    reset,
    watch
  } = useForm<EditSubscriptionData>({
    defaultValues: initialValues,
    mode: 'onChange',
    resolver: editSubscriptionResolver as any
  });

  useEffect(() => {
    if (initialValues) {
      reset({
        amount: initialValues.amount,
        giftRecurringOption: initialValues.giftRecurringOption,
        dueDate: initialValues.dueDate,
        key: tokenizedCard
          ? safeJsonParse<TokenizedCreditCardData>(tokenizedCard)?.card_key
          : tokenizedACH
            ? safeJsonParse<TokenizedACHData>(tokenizedACH)?.check_key
            : undefined
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  useEffect(() => {
    if (tokenizedCard) {
      setTokenizedCard(safeJsonParse<TokenizedCreditCardData>(tokenizedCard));
      setValue('key', safeJsonParse<TokenizedCreditCardData>(tokenizedCard)?.card_key);
      setTokenizedACH(undefined);
    }
    if (tokenizedACH) {
      setTokenizedACH(safeJsonParse<TokenizedACHData>(tokenizedACH));
      setValue('key', safeJsonParse<TokenizedACHData>(tokenizedACH)?.check_key);
      setTokenizedCard(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenizedCard, tokenizedACH]);

  const valueWatch = watch('amount');
  const giftRecurringOptionWatch = watch('giftRecurringOption');

  const onChangePaymentMethod = async () => {
    try {
      await getNonProfitPaymentMethod(
        { id: npoId },
        {
          onSuccess: async (response) => {
            const parsedSubscription = safeJsonParse<Subscription>(subscriptionItem ?? '');
            const formValues = getValues();
            const info = {
              ...parsedSubscription,
              amount: formValues.amount,
              dueDate: formValues.dueDate,
              billingPeriod: formValues.giftRecurringOption
            };
            const payload = {
              routeWithSelectedMethod: '/(app)/donationPayment/editSubscription',
              subscriptionItem: JSON.stringify(info),
              availableMethods: response.paymentMethods
            };

            const encrypted = await encryptObject(payload);

            router.navigate({
              pathname: '/(app)/paymentMethods',
              params: {
                payload: encodeURIComponent(encrypted)
              }
            });
          }
        }
      );
    } catch (e) {
      console.error(e);
    }
  };

  const onSubmit: SubmitHandler<EditSubscriptionData> = async (data) => {
    try {
      onSubmitTap(data);
    } catch (error) {
      setError('root', { message: `${error}` });
    }
  };

  return (
    <View style={style.mainContainer}>
      {/* Supporting */}
      <View style={style.dataRow}>
        <Text fontWeight="700" style={style.supporting}>
          {parsedSubscriptionItem?.nonProfitOrg.name}
        </Text>
      </View>

      {/* Amount */}
      <CurrencyInputField control={control} name="amount" />
      <View style={style.currencyOptions}>
        {predefinedValues.map((value, index) => {
          return (
            <BrandedChip
              key={index}
              isSelected={value === valueWatch}
              value={`$${value}`}
              onPress={() => setValue('amount', value)}
            />
          );
        })}
      </View>

      <Divider />

      {/* Start Date */}
      <Text fontWeight="700" style={style.donationDate}>
        {i18n.t('mix.date')}
      </Text>
      <DatePickerInputField
        control={control}
        name={'dueDate'}
        variant="rounded_with_shadow"
        timePicker={false}
        minDate={new Date().setHours(0, 0, 0, 0)}
      />

      <Divider />

      {/* Recurring Option */}
      <Text fontWeight="700" style={style.giftTypes}>
        {i18n.t('donation.recurring_option')}
      </Text>
      <View style={style.recurringOptions}>
        {giftRecurringOptions.map((option, index) => {
          return (
            <BrandedChip
              key={index}
              isSelected={option.value === giftRecurringOptionWatch}
              value={option.label}
              onPress={() => setValue('giftRecurringOption', option.value)}
              fontSize={12}
              borderRadius={5}
            />
          );
        })}
      </View>

      {/* Payment Method */}
      {(parsedTokenizedCard || parsedTokenizedACH) && (
        <View style={style.dataRow}>
          <Text style={style.title} fontWeight="700">
            {i18n.t('donation_summary.payment_method')}
          </Text>
        </View>
      )}
      {parsedTokenizedCard && (
        <TokenizedCardView
          tokenizedCard={parsedTokenizedCard}
          showDelete={false}
          showChange={true}
          onPress={onChangePaymentMethod}
        />
      )}
      {parsedTokenizedACH && (
        <TokneizedACHView
          tokenizedAch={parsedTokenizedACH}
          showDelete={false}
          showChange={true}
          onPress={onChangePaymentMethod}
        />
      )}

      <BrandedButton loading={loading} disabled={!isValid || isPending} onPress={handleSubmit(onSubmit)}>
        {confirm}
      </BrandedButton>
    </View>
  );
};

const style = StyleSheet.create({
  mainContainer: {
    flex: 1,
    gap: 25
  },

  title: {
    fontSize: 22,
    color: 'black'
  },

  subtitle: {
    color: 'black',
    opacity: 0.6
  },

  currencyOptions: {
    flexDirection: 'row',
    gap: 10,
    justifyContent: 'center'
  },

  recurringOptions: {
    flexDirection: 'row',
    gap: 5,
    justifyContent: 'center'
  },

  giftTypes: {
    fontSize: 22,
    color: 'black'
  },

  donationDate: {
    fontSize: 22,
    color: 'black'
  },

  iconColor: {
    color: '#808080'
  },

  searchNpoContainer: {
    height: 38,
    zIndex: 1000
  },

  dataRow: {
    flexDirection: 'row',
    gap: 10,
    alignItems: 'center'
  },

  supporting: {
    fontSize: 22,
    color: 'black'
  }
});

export default EditSubscriptionForm;
